// frontend/src/utils/swingPatterns.js
//
// Clean-room implementation of swing high/low detection plus classic single/two-candle
// reversal pattern classification at each confirmed swing point. This is an original
// implementation of well-known, generic technical-analysis techniques (symmetric pivot
// lookback, hammer/engulfing/shooting-star family patterns) — not derived from any
// third-party script or codebase.

const PATTERNS = {
  hammer: {
    title: 'Hammer',
    description: 'Short body with a long lower wick, found at the bottom of a downtrend — signals buyers overpowered sellers intraday.',
  },
  ihammer: {
    title: 'Inverted Hammer',
    description: 'Short body with a long upper wick, found at the bottom of a downtrend — signals a fading sell-off and a potential reversal up.',
  },
  bulleng: {
    title: 'Bullish Engulfing',
    description: 'A small red candle fully engulfed by the next green candle — buyers decisively overwhelmed the prior sellers.',
  },
  hanging: {
    title: 'Hanging Man',
    description: 'Short body with a long lower wick, found at the top of an uptrend — signals a sharp intraday sell-off buyers only partly recovered from.',
  },
  shooting: {
    title: 'Shooting Star',
    description: 'Short body with a long upper wick, found at the top of an uptrend — signals a rally that failed and reversed before the close.',
  },
  beareng: {
    title: 'Bearish Engulfing',
    description: 'A small green candle fully engulfed by the next red candle — sellers decisively overwhelmed the prior buyers.',
  },
};

/**
 * Detect confirmed swing highs/lows over a symmetric `length`-bar window, and classify
 * the candle at each swing point against six classic reversal patterns.
 *
 * A bar is a swing high/low only once `length` bars exist on both sides of it, so each
 * point is confirmed `length` bars after it actually occurred (standard swing-detection lag).
 *
 * @param {Array<{time:number, open:number, high:number, low:number, close:number}>} candles
 * @param {number} length - bars required on both sides to confirm a swing point (default 21)
 * @returns {Array<{index:number, time:number, price:number, type:'high'|'low', label:'HH'|'LH'|'LL'|'HL', pattern:{title:string, description:string}|null}>}
 */
export function detectSwingPatterns(candles, length = 21) {
  if (!Array.isArray(candles) || candles.length < length * 2 + 1) return [];

  const points = [];
  let lastSwingHigh = null;
  let lastSwingLow = null;

  for (let i = length; i < candles.length - length; i++) {
    const bar = candles[i];
    const prevBar = candles[i - 1];
    if (!bar || !prevBar) continue;

    let isPivotHigh = true;
    let isPivotLow = true;
    for (let j = i - length; j <= i + length; j++) {
      if (j === i) continue;
      if (candles[j].high >= bar.high) isPivotHigh = false;
      if (candles[j].low <= bar.low) isPivotLow = false;
      if (!isPivotHigh && !isPivotLow) break;
    }

    if (!isPivotHigh && !isPivotLow) continue;

    const o = bar.open, h = bar.high, l = bar.low, c = bar.close;
    const bodySize = Math.abs(c - o);

    let pattern = null;
    if (isPivotLow) {
      if (Math.min(o, c) - l > bodySize && h - Math.max(c, o) < bodySize) {
        pattern = PATTERNS.hammer;
      } else if (h - Math.max(c, o) > bodySize && Math.min(c, o) - l < bodySize) {
        pattern = PATTERNS.ihammer;
      } else if (c > o && prevBar.close < prevBar.open && c > prevBar.open && o < prevBar.close) {
        pattern = PATTERNS.bulleng;
      }

      const label = lastSwingLow !== null && l < lastSwingLow ? 'LL' : 'HL';
      lastSwingLow = l;
      points.push({ index: i, time: bar.time, price: l, type: 'low', label, pattern });
    } else {
      if (Math.min(c, o) - l > bodySize && h - Math.max(o, c) < bodySize) {
        pattern = PATTERNS.hanging;
      } else if (h - Math.max(o, c) > bodySize && Math.min(c, o) - l < bodySize) {
        pattern = PATTERNS.shooting;
      } else if (c < o && prevBar.close > prevBar.open && c < prevBar.open && o > prevBar.close) {
        pattern = PATTERNS.beareng;
      }

      const label = lastSwingHigh !== null && h > lastSwingHigh ? 'HH' : 'LH';
      lastSwingHigh = h;
      points.push({ index: i, time: bar.time, price: h, type: 'high', label, pattern });
    }
  }

  return points;
}
